import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect, useMemo } from 'react';

import useAxiosPrivateKIS from '../../../../hooks/useAxiosPrivateKIS';

const StepOne = ({ data, onUpdate, policyId, errors, mutualGbs, claim = null }) => {
  const axios = useAxiosPrivateKIS()

  const [staffList, setStaffList] = useState({});
  const [conditions, setConditions] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(data.selectedStaff || null)
  const [selectedCondition, setSelectedCondition] = useState(data.selectedCondition || '')
  const [absenceStart, setAbsenceStart] = useState(data?.absenceStart || '')
  const [absenceEnd, setAbsenceEnd] = useState(data?.absenceEnd || '')
  const [showPhased, setShowPhased] = useState(false)
  const [showConfidential, setShowConfidential] = useState(false)
  const [isPhasedReturn, setIsPhasedReturn] = useState(false)
  const [isShielding, setIsShielding] = useState(false)
  const [isConfidential, setIsConfidential] = useState(false)
  const [status, setStatus] = useState(null)

  const [dateErrors, setDateErrors] = useState({
    startBeforeEmployment: null,
  })

  const [filteredConditions, setFilteredConditions] = useState([]);
  const [isLoadingStepOneData, setIsLoadingStepOneData] = useState(false)

  useEffect(() => {
    onUpdate({
      selectedStaff,
      selectedCondition,
      absenceStart,
      absenceEnd,
      isPhasedReturn,
      isShielding,
      isConfidential
    });
  }, [
    selectedStaff,
    selectedCondition,
    absenceStart,
    absenceEnd,
    isPhasedReturn,
    isShielding,
    isConfidential
  ])

  useEffect(() => {
    if (!policyId) return
    setIsLoadingStepOneData(true)
    const fetchAll = async () => {
      try {
        await Promise.all([fetchStaffList(), fetchConditions()])
      } finally {
        setIsLoadingStepOneData(false)
      }
    }
    fetchAll()
  }, [policyId]);

  useEffect(() => {
    if (!selectedStaff || !staffList) return;

    if (selectedStaff.maternityCover) {
      setFilteredConditions(conditions);
    } else {
      const updatedConditions = conditions.map((condition) => ({
        ...condition,
        disabled: condition.condition_title.toLowerCase().includes('maternity'),
      }));
      setFilteredConditions(updatedConditions)
    }
  }, [selectedStaff, conditions])

  useEffect(() => {
    if (selectedCondition) {
      if (selectedCondition.condition_phased === 1) {
        setShowPhased(true);
      } else {
        setShowPhased(false);
        setIsPhasedReturn(false);
        setIsShielding(false);
        setShowConfidential(false);
        setIsConfidential(false);
      }

      if (selectedCondition.condition_id === 'ed685ef6-7b54-11e7-a963-336a7d328401') {
        setShowConfidential(true);
      }
    } else {
      setShowPhased(false);
      setIsPhasedReturn(false);
      setIsShielding(false);
      setShowConfidential(false);
      setIsConfidential(false);
    }
  }, [selectedCondition])

  useEffect(() => {
    const errors = {};
    if (selectedStaff?.start_date && absenceStart) {
      const staffStart = new Date(selectedStaff.start_date)
      const absenceStartDate = new Date(absenceStart)

      if (absenceStartDate < staffStart) {
        errors.startBeforeEmployment = `The first date of absence must be after ${staffStart.toLocaleDateString('en-GB')}`
      }
    }

    setDateErrors(errors)
  }, [absenceStart, selectedStaff])

  const fetchStaffList = async () => {
    try {
      const res = await axios.get(`/api/v1/staff-hub/policy_staffs/get-staff-list/${policyId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        params: { claim_id: claim?.id || 0 }
      });
      let response = res.data;
      setStaffList(response);
    } catch (err) {
      console.error('Error fetching staff list:', err);

    }
  };

  //maybe i need to fetch the conditions based on the selected staff member?
  // Grey out the inputs till the staff member is selected
  const fetchConditions = async () => {
    try {
      const response = await axios.get(`/api/v1/conditions?api_token=${process.env.REACT_APP_KIS_TOKEN}&ascending=1&pid=${policyId}`);
      const rawConditions = response.data.data;

      if (typeof rawConditions !== 'object') {
        console.error('Unexpected format for conditions');
        return;
      }

      const conditionArray = Object.values(rawConditions).sort((a, b) =>
        a.condition_title.localeCompare(b.condition_title)
      );
      setConditions(conditionArray);
    } catch (err) {
      console.error('Error fetching conditions:', err);
    }
  }

  const allowedStatuses = useMemo(
    () => new Set([0, 1, 2, 3, 4, 5, 12, 13, 14, 15, 16, 28, 33, 35]),
    []
  )

  const isEndDateDisabled = useMemo(() => {
    if (claim?.claim_id && claim?.claim_absence_start_date) {
      return !allowedStatuses.has(Number(claim?.claimstatus));
    }
    return true;
  }, [claim?.claim_id, claim?.claim_absence_start_date, claim?.claimstatus]);


  const handleStaffChange = (e) => {
    const newStaff = JSON.parse(e.target.value)

    setSelectedStaff(newStaff)
    setAbsenceStart('')
    setAbsenceEnd('')
    setSelectedCondition(null)
    setShowPhased(false)
    setIsPhasedReturn(false)
    setIsShielding(false)
    setShowConfidential(false)
    setIsConfidential(false)

    onUpdate({
      selectedStaff: newStaff,
      selectedCondition,
      absenceStart: '',
      absenceEnd: '',
      isPhasedReturn,
      isShielding,
      isConfidential
    })
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {isLoadingStepOneData && (
        <div className="col-span-2 inline-flex items-center gap-2 text-sm text-sas">
          <span>Loading...</span>
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">Staff Name <span className='text-red-600'>*</span></label>
        <select
          required
          value={selectedStaff ? JSON.stringify(selectedStaff) : ''}
          onChange={handleStaffChange}
          disabled={isLoadingStepOneData}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="" disabled>Select a staff member</option>
          {Object.values(staffList).map((staff, index) => {
            const fte = staff.fte || '';
            const dob = staff.date_of_birth ? new Date(staff.date_of_birth).toLocaleDateString('en-GB') : ''

            return (
              <option key={index} value={JSON.stringify(staff)}>
                {`${staff.staff_name} - ${fte} - ${dob}`}
              </option>
            )
          })}
        </select>
      </div>
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">Condition <span className='text-red-600'>*</span></label>
        <select
          required
          value={selectedCondition ? JSON.stringify(selectedCondition) : ''}
          onChange={(e) => setSelectedCondition(JSON.parse(e.target.value))}
          disabled={isLoadingStepOneData}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="" disabled>Select a condition</option>
          {filteredConditions.map((cond) => (
            <option key={cond.condition_id} value={JSON.stringify(cond)} disabled={cond.disabled}>
              {cond.condition_title}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">First full day of absence <span className='text-red-600'>*</span></label>
        <input
          type="date"
          value={absenceStart}
          min={selectedStaff?.start_date || ''}
          max={selectedStaff?.end_date || absenceEnd || ''}
          onChange={(e) => setAbsenceStart(e.target.value)}
          disabled={isLoadingStepOneData}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">Last day of absence (only select this date if the staff member has returned)</label>
        <input
          type="date"
          value={absenceEnd}
          min={absenceStart || selectedStaff?.start_date || ''}
          max={selectedStaff?.end_date || ''}
          onChange={(e) => setAbsenceEnd(e.target.value)}
          disabled={isLoadingStepOneData}
          // disabled={isEndDateDisabled}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>
      {selectedStaff?.staff_id && selectedCondition && !errors?.invalidCondition && (
        <>
          {!mutualGbs && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form field-switch" style={{ color: 'red' }}>
                <label htmlFor="claim-shielding">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-shielding"
                    checked={isShielding}
                    disabled={isLoadingStepOneData}
                    onChange={(e) => setIsShielding(e.target.checked)}
                  />
                  Is staff member shielding?
                </label>
              </div>
            </fieldset>
          )}

          {/* Confidential */}
          {showConfidential && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form field-switch" style={{ color: 'grey' }}>
                <label htmlFor="claim-confidential">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-confidential"
                    checked={isConfidential}
                    disabled={isLoadingStepOneData}
                    onChange={(e) => setIsConfidential(e.target.checked)}
                  />
                  Is this claim confidential? (this will hide the reason for absence for all users)
                </label>
              </div>
            </fieldset>
          )}

          {/* Phased return */}
          {showPhased && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form phased-return-switch field-switch">
                <label htmlFor="claim-claim_phased">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-claim_phased"
                    checked={isPhasedReturn}
                    disabled={isLoadingStepOneData}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsPhasedReturn(checked);
                      // if (checked) {
                      //   setAbsenceEnd('')
                      // }
                    }}
                  />
                  Is this a phased return?
                </label>
              </div>
            </fieldset>
          )}
        </>
      )}

      {errors?.missingFields && (
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.missingFields}</strong>
        </div>
      )}

      {errors?.incomplete && (
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.incomplete}</strong>
        </div>
      )}

      {errors?.startDate && (
        <div className="bg-red-100 border border-red-300 text-red-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.startDate}</strong>
        </div>
      )}

      {errors?.endDate && (
        <div className="bg-red-100 border border-red-300 text-red-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.endDate}</strong>
        </div>
      )}

      {errors?.invalidCondition && (
        <div className="bg-blue-100 border border-blue-300 text-blue-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.invalidCondition}</strong><br />
          Please contact your Account Manager on 01773 814400 or speak to us using our Live Chat.
        </div>
      )}
    </div>
  )
};

export default StepOne;

